import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Sunrise, Loader2, Sparkles, AlertTriangle, ListChecks, Mail, BellRing, RefreshCw } from "lucide-react";
import { fmtValue, fmtDate, SEV_STYLE } from "./shared";

// Morning Executive Digest — shows the latest auto-generated briefing and lets a
// CEO regenerate + deliver it on demand (email + mobile push + in-app).
export function MorningDigestPanel({ params, canRun }) {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    execService.digest(params)
      .then(setDigest)
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const run = () => {
    setRunning(true);
    execService.runDigest({ ...params, deliver: true })
      .then((d) => { setDigest(d); toast.success("Digest generated and delivered"); })
      .catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail)))
      .finally(() => setRunning(false));
  };

  return (
    <section className="bg-card border border-border rounded-xl overflow-hidden" data-testid="morning-digest-panel">
      <div className="flex items-center justify-between gap-3 p-4 bg-gradient-to-r from-primary/10 to-accent/5 border-b border-border flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Sunrise className="h-5 w-5" />
          </span>
          <div>
            <h2 className="text-base font-bold text-foreground">Morning Executive Digest</h2>
            <p className="text-xs text-muted-foreground">
              {digest ? `${digest.dateLabel} · generated ${fmtDate(digest.generatedAt)}` : "Auto-generated each morning · email + mobile push"}
            </p>
          </div>
        </div>
        {canRun && (
          <button onClick={run} disabled={running}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 disabled:opacity-60"
            data-testid="digest-run-button">
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {running ? "Generating…" : digest ? "Regenerate now" : "Generate now"}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : !digest ? (
        <div className="p-8 text-center" data-testid="digest-empty">
          <p className="text-sm text-foreground font-medium">No digest yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            The briefing runs automatically every morning. {canRun ? "Generate one now to preview." : "Check back after the morning run."}
          </p>
        </div>
      ) : (
        <div className="p-4 space-y-4">
          {/* AI summary */}
          <div className="rounded-lg bg-accent/5 border border-accent/20 p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs font-bold text-accent">AI Summary</span>
              {digest.confidence > 0 && <span className="text-[11px] text-muted-foreground">· {Math.round(digest.confidence * 100)}% confidence</span>}
            </div>
            <p className="text-sm text-foreground leading-relaxed">{digest.summary}</p>
          </div>

          {/* headline tiles */}
          {digest.headline?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="digest-headline">
              {digest.headline.slice(0, 8).map((t, i) => (
                <div key={i} className="bg-muted/40 rounded-lg p-2.5">
                  <p className="text-base font-bold text-foreground">{fmtValue(t.value, t.format)}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{t.label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {/* alerts */}
            <div>
              <div className="flex items-center gap-1.5 mb-2"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /><span className="text-xs font-bold text-foreground">Top Alerts</span></div>
              {digest.alerts?.length ? (
                <ul className="space-y-1.5" data-testid="digest-alerts">
                  {digest.alerts.slice(0, 5).map((a, i) => (
                    <li key={i} className={`text-xs rounded-md border px-2.5 py-1.5 ${SEV_STYLE[a.severity] || SEV_STYLE.low}`}>
                      <span className="font-semibold">{a.title}</span>
                      {a.message && <span className="opacity-80"> — {a.message}</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-muted-foreground">No critical alerts.</p>}
            </div>
            {/* action plan */}
            <div>
              <div className="flex items-center gap-1.5 mb-2"><ListChecks className="h-3.5 w-3.5 text-primary" /><span className="text-xs font-bold text-foreground">Today's Action Plan</span></div>
              {digest.actionPlan?.length ? (
                <ul className="space-y-1.5" data-testid="digest-actions">
                  {digest.actionPlan.slice(0, 6).map((x, i) => (
                    <li key={i} className="text-xs text-foreground bg-muted/40 rounded-md px-2.5 py-1.5">
                      <span className="font-medium">{x.title || x.label}</span>
                      {x.detail && <span className="text-muted-foreground"> — {x.detail}</span>}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-xs text-muted-foreground">No actions queued.</p>}
            </div>
          </div>

          {/* delivery status */}
          {digest.delivery && (
            <div className="flex items-center gap-4 pt-1 text-[11px] text-muted-foreground border-t border-border" data-testid="digest-delivery">
              <span className="inline-flex items-center gap-1 pt-2"><BellRing className="h-3 w-3" /> In-app & push: {digest.delivery.inApp ?? 0} recipient(s)</span>
              <span className="inline-flex items-center gap-1 pt-2"><Mail className="h-3 w-3" /> Email: {digest.delivery.email || "—"}</span>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
